import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation, Trans } from "react-i18next";
import { useBiometricConsent } from "../../../hooks/faceVerification/useBiometricConsent";
import { trackClicked } from "../../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../../shared/analytics/documentCaptureEvents";
import type { BlockedPermission } from "../../../hooks/faceVerification/useFaceVerification";
import faceVerificationStyles from "../FaceVerification.module.scss";
import styles from "./IProovSlots.module.scss";
import { logger } from "../../../shared/utils/logger";

interface IProovSlotsProps {
  sessionToken: string | null;
  consentEnabled?: boolean;
  onAcceptConsent?: () => Promise<void>;
  onConsentFailed?: (error: Error) => void;
  isSessionTokenExpiringSoon?: () => boolean;
  onConsentGetFailed?: (error: Error) => void;
  /**
   * Camera/motion permission recovery (VEX-7599): reloads the host (top) page so
   * the browser re-prompts — the only reliable re-prompt on iOS Safari. Wired to
   * the permission_denied slot's Reload button (previously a dead button).
   */
  onReload?: () => void | Promise<void>;
  /** Retry in place from iProov's own failed slot (VEX-7653). */
  onRetry?: () => void | Promise<void>;
  /**
   * Host's retry decision: null while pending, false once the budget is spent. The
   * button stays disabled until the host answers, because only it can see the budget.
   */
  retryAllowed?: boolean | null;
  /**
   * iProov has one `permission_denied` slot for both denials, so without this a motion
   * denial tells the member to grant webcam access — the wrong setting. Defaults to
   * the camera wording.
   */
  blockedPermission?: BlockedPermission | null;
}

/**
 * IProovSlots Component
 *
 * Contains all iProov slot templates for the face verification flow.
 * These slots customize the iProov SDK UI at different stages of the verification process.
 */
export function IProovSlots({ sessionToken, consentEnabled, onAcceptConsent, onConsentFailed, isSessionTokenExpiringSoon, onConsentGetFailed, onReload, onRetry, retryAllowed, blockedPermission }: IProovSlotsProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { error, consentText, fetchConsent, clearError } = useBiometricConsent();

  // Waiting on the host's retry decision. Distinct from a refusal (retryAllowed === false),
  // which also disables the button but is not a transient state to narrate. `undefined`
  // counts as pending too, so a caller that wires onRetry without the flag still gets a
  // button that explains itself rather than one that just sits there dead.
  const isRetryPending = !!onRetry && retryAllowed !== true && retryAllowed !== false;

  const handleBiometricPolicyClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.OPEN_BIOMETRIC_POLICY, "link", { section: DOCUMENT_CAPTURE_SECTIONS.INFO, targetAction: "open" });

    if (!sessionToken || sessionToken.trim() === '') {
      return;
    }

    if (isSessionTokenExpiringSoon?.()) {
      onConsentGetFailed?.(Object.assign(new Error('Session token has expired'), { httpStatus: 401 }));
      return;
    }

    try {
      await fetchConsent(sessionToken);
      setIsModalOpen(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch consent');
      logger.error('[IProovSlots] Consent policy fetch failed:', error.message);
      onConsentGetFailed?.(error);
    }
  };

  const handleOpenCameraClick = async () => {
    // Analytics: record ONLY the tap. The capture view + `capture` stage wait for iProov
    // `started` (handleStarted), so a cancelled motion prompt logs no capture never seen.
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.START_FACE_CAPTURE, "button", { section: DOCUMENT_CAPTURE_SECTIONS.INFO, targetAction: "open" });

    if (!consentEnabled || !onAcceptConsent) return;

    if (isSessionTokenExpiringSoon?.()) {
      onConsentGetFailed?.(Object.assign(new Error('Session token has expired'), { httpStatus: 401 }));
      return;
    }

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Consent request timed out')), 3500)
    );

    try {
      await Promise.race([onAcceptConsent(), timeoutPromise]);
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Consent failed');
      logger.error('[IProovSlots] Consent failed or timed out:', err.message);
      if (onConsentFailed) {
        onConsentFailed(err);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    clearError();
  };


  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) handleCloseModal();
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isModalOpen]);

  return (
    <>
      {/* Ready slot - initial state when SDK loads */}
      <div slot="ready" className="ready-slot-container">
        <p className="heading-verification">{t('face.ready.heading')}</p>

        <div className="illustration-container">
          <img src="/assets/svg/selfie.svg" className="selfie-illustration" role="none" alt="" />
        </div>

        <div className="info-items-container">
          <div className="info-items">
            <div className="info-item">
              <img src="/assets/svg/icon-camera.svg" className="info-icon" role="none" alt="" />
              <p className="info-text">{t('face.ready.info.camera')}</p>
            </div>

            <div className="info-item">
              <img src="/assets/svg/icon-brightness.svg" className="info-icon" role="none" alt="" />
              <p className="info-text">{t('face.ready.info.brightness')}</p>
            </div>
          </div>
        </div>

        {/* Consent text — only shown when consent_enabled is true */}
        {consentEnabled && (
          <div style={{ textAlign: 'left', padding: '0 24px 8px' }}>
            <span className={styles.consentText}>
              <Trans
                i18nKey="face.ready.consentText"
                components={[
                  <button
                    key="link"
                    type="button"
                    onClick={handleBiometricPolicyClick}
                    disabled={!sessionToken}
                    className={styles.consentLink}
                  />
                ]}
              />
            </span>
          </div>
        )}

      </div>

      {/* Button slot - launches fullscreen and starts iProov */}
      <div slot="button" className="button-slot-container" style={{ paddingTop: '8px' }}>
        <button
          type="button"
          className="btn btn-primary"
          // Capture phase, not bubble: iProov starts the transaction from its own listener
          // on this button, and `started` can fire (emitting the capture view) before the
          // click reaches React's root handler — which timestamped the view ahead of this
          // tap whenever camera permission was already granted.
          onClickCapture={handleOpenCameraClick}
        >
          {t('face.ready.button')}
        </button>
      </div>

      {/* Biometric Policy modal — only when consent_enabled */}
      {consentEnabled && isModalOpen && createPortal(
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{t('face.biometricPolicy.title')}</h2>
              <button onClick={handleCloseModal} className={styles.closeButton} aria-label="Close modal">×</button>
            </div>
            <div className={styles.modalBody}>
              {error ? (
                <p style={{ color: '#dc3545' }}>{error}</p>
              ) : consentText ? (
                <div dangerouslySetInnerHTML={{ __html: consentText }} />
              ) : (
                <p>Loading consent policy...</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Grant permission slot */}
      <div slot="grant_permission">
        <div className="grant-permission-icon">
          <img src="/assets/svg/camera-transparent.svg" width="48" height="48" role="none" alt="" />
        </div>
        <p className="grant-permission-heading">{t('face.grantPermission.heading')}</p>
        <p className="grant-permission-subtitle">{t('face.grantPermission.subtitle')}</p>
      </div>

      {/* Grant button slot */}
      <div slot="grant_button" className="grant-button-container">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.ALLOW_CAMERA_ACCESS, "button", { section: DOCUMENT_CAPTURE_SECTIONS.CAMERA_PERMISSION, targetAction: "open" })}
        >
          {t('face.grantPermission.button')}
        </button>
      </div>

      {/* Progress slot - shown during processing */}
      <div slot="progress">
        <div data-target="processing">
          <p className="heading">{t('face.progress.heading')}</p>
          <div className={faceVerificationStyles.spinner}></div>
        </div>
      </div>

      {/* Passed slot - shown on successful authentication */}
      <div slot="passed">
        <div data-target="processing">
          <p className="heading">{t('face.passed.heading')}</p>
          <div className={faceVerificationStyles.spinner}></div>
        </div>
      </div>

      {/* Canceled slot - user exits before completion */}
      <div slot="canceled">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <div className="heading iproov-lang-term"></div>
        <p className="iproov-lang-heading"></p>
        <button type="button" className="btn btn-primary">
          {t('face.canceled.button')}
        </button>
      </div>

      {/* Error slot - authentication errors */}
      <div slot="error">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <div className="heading iproov-lang-term"></div>
        <p className="iproov-lang-heading"></p>
        <button type="button" className="btn btn-primary">
          {t('face.error.button')}
        </button>
      </div>

      {/* Failed slot - authentication fails */}
      {/* iProov populates .iproov-lang-term / .iproov-lang-heading with its own
          localized failure copy. EXPERIMENT (VEX-7653): the button used to be a bare
          window.location.reload() — the same blunt approach VEX-7599 had to replace for
          permission_denied, and worse here, since a reload re-inits the whole app when
          all we need is a fresh iProov token. onRetry mints one and remounts in place;
          it falls back to the reload when no handler is wired. */}
      <div slot="failed">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        {/* Order is deliberate. iProov inject the TITLE into .iproov-lang-heading and
            the REASON LIST into .iproov-lang-term — the opposite of what the class
            names suggest — so with the markup the other way round the heading rendered
            underneath the reasons. They select by class, not position, so reordering is
            safe. */}
        <p className="heading-failed iproov-lang-heading"></p>
        {/* `.failure-reasons` carries the box treatment, NOT `.iproov-lang-term`, which
            the canceled, error, no_camera and unsupported slots share — styling the
            iProov class directly would put a grey card round all of them too. */}
        <div className="failure-reasons iproov-lang-term"></div>
        {/* While the host decides (retryAllowed === null) the button is disabled, which
            is the member's FIRST sight of this screen — a dead "Try again" reads as
            broken. Say what is actually happening instead, the way iProov's own portal
            does. Only the pending state relabels: a refusal (false) keeps "Try again"
            disabled, since the host is about to take over the screen anyway. */}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!!onRetry && retryAllowed !== true}
          aria-busy={isRetryPending || undefined}
          onClick={() => {
            // Analytics: record the retry tap before onRetry/reload tears this context down
            trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.RETRY_FACE_CAPTURE, "button", { section: DOCUMENT_CAPTURE_SECTIONS.CAPTURE, targetAction: "navigate" });
            onRetry ? void onRetry() : window.location.reload();
          }}
        >
          {isRetryPending ? t('face.failed.pendingButton') : t('face.failed.button')}
        </button>
      </div>

      {/* Permission denied slot. The button reloads the HOST (top) page — the only
          reliable way to re-prompt camera/motion on iOS Safari (VEX-7599). Falls
          back to an iframe reload if no host-reload handler was provided. */}
      <div slot="permission_denied">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        {blockedPermission === "motion" ? (
          <>
            <p className="heading">{t('face.permissionDenied.motionHeading')}</p>
            <p>{t('face.permissionDenied.motionBody')}</p>
          </>
        ) : (
          <p className="heading">{t('face.permissionDenied.heading')}</p>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => (onReload ? onReload() : window.location.reload())}
        >
          {t('face.permissionDenied.button')}
        </button>
      </div>

      {/* No camera slot */}
      <div slot="no_camera">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <div className="heading iproov-lang-term"></div>
        <p className="iproov-lang-heading"></p>
      </div>

      {/* Unsupported slot */}
      <div slot="unsupported">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <div className="heading iproov-lang-term"></div>
        <p className="iproov-lang-heading"></p>
        <button type="button" className="btn btn-primary">
          Continue
        </button>
      </div>

      {/* Rotate portrait slot */}
      <div slot="rotate_portrait">
        <p className="heading">{t('face.rotatePortrait.heading')}</p>
      </div>
    </>
  );
}
